import { d as disableTabulationOnNotActiveSlidesWithModel } from "./disableTabulationOnNotActiveSlidesWithModel-5413c83c.js";

const selectors = {
  section: "[data-section-type='product']",
  container: "[data-js-product-form]",
  product: "[data-js-product-json]",
  skuWrapper: ".js-product-sku-wrapper",
  sku: ".js-product-sku",
  price: ".js-price",
  productPrice: "[data-product-price]",
  productPriceOld: "[data-price-old]",
  priceUnit: "[data-js-unit-price]",
  productSalePercentageBadge: ".js-sale-percentage-badge",
  productSalePercentage: "[data-sale-percentage]",
  variantId: '[name="id"]',
  submit: '[type="submit"]',
  quantityError: ".js-product-quantity-error",
  variants: "[data-js-product-variant]",
  variantElemJSON: "[data-selected-variant]",
  isPreset: "[data-is-preset]",
  productInventoryWrapper: ".js-product-inventory-wrapper",
  swatchPreview: ".js-swatch-preview",
  stickyBar: ".js-sticky-add-to-cart",
  stickyBarButton: ".js-sticky-add-to-cart-button",
  recipientCheckbox: ".js-recipient-form-checkbox",
  recipientFieldsContainer: ".js-recipient-form-fields",
  recipientField: ".js-recipient-form-field",
  recipientTimeZoneOffset: ".js-recipient-form-timezone-offset",
  recipientNoJsControl: ".js-recipient-form-no-js-control",
  customFieldGroup: ".js-custom-field-block",
  customFieldInput: ".js-custom-field-input",
  customFieldCheckbox: ".js-custom-field-checkbox",
  animate: ".js-animate",
  breaksVal: ".js-price-breaks-val",
  volumePricing: ".js-product-volume-pricing",
  quantityRules: ".js-product-quantity-rules",
  discountButton: ".js-discount-button",
  discountStatus: "[data-discount-button-status]",
  discountCode: ".js-discount-code"
};

const attributes = {
  id: "id",
  isCurrencyEnabled: "data-currency-code-enabled",
  discountStatus: "data-discount-button-status"
};

let classes = {};
const cachedOptions = new Map();
let fetchController = new AbortController();

const ProductForm = () => {
  let convertFormData;
  let QuantityWidget;
  let cssClasses;
  let formatMoney;
  let getUrlWithVariant;
  let containers = [];
  let forms = [];

  function init() {
    convertFormData = window.themeCore.utils.convertFormData;
    QuantityWidget = window.themeCore.utils.QuantityWidget;
    cssClasses = window.themeCore.utils.cssClasses;
    formatMoney = window.themeCore.utils.formatMoney;
    getUrlWithVariant = window.themeCore.utils.getUrlWithVariant;

    classes = {
      ...cssClasses,
      onSale: "price--on-sale",
      hidePrice: "price--hide",
      animate: "js-animate",
      animated: "animated"
    };

    containers = [...document.querySelectorAll(selectors.container)];
    forms = setForms();
    setEventListeners();
    initFirstState();
    showOptions();
    initStickyBar();
    initRecipientForm();
    initDiscountCodeButton();
  }

  function setForms() {
    return containers.reduce((acc, container) => {
      const productJsonTag = container.querySelector(selectors.product);
      if (!productJsonTag) return acc;

      let product;
      try {
        product = JSON.parse(productJsonTag.textContent);
      } catch {
        return acc;
      }

      const id = container.getAttribute(attributes.id);
      const submit = [...container.querySelectorAll(selectors.submit)];
      const skuContainer = container.querySelector(selectors.sku);
      const priceContainers = [...container.querySelectorAll(selectors.price)];
      const quantityError = container.querySelector(selectors.quantityError);
      const variantIdContainer = container.querySelector(selectors.variantId);

      const quantityWidgetEl = QuantityWidget(container, {
        onQuantityChange: () => updateErrorMessages({ quantityError })
      }).init();

      const customFieldBlocks = [...container.querySelectorAll(selectors.customFieldGroup)];
      const customFieldElements = customFieldBlocks.map(block => ({
        input: block.querySelector(selectors.customFieldInput),
        checkbox: block.querySelector(selectors.customFieldCheckbox)
      }));

      acc.push({
        id,
        container,
        product,
        elements: {
          skuContainer,
          priceContainers,
          quantityWidgetEl,
          quantityError,
          variantIdContainer,
          submit,
          mobileSkuContainer: null,
          customFieldElements
        }
      });

      return acc;
    }, []);
  }

  function setEventListeners() {
    forms.forEach(({ container }) => {
      container.addEventListener("change", onChangeForm);
      container.addEventListener("submit", onFormSubmit);
    });

    window.themeCore.EventBus.listen("cart:updated", cartData => {
      if (!cartData?.items) return;
      forms.forEach(formEntity => {
        const variant = getCurrentVariantFromForm(formEntity);
        if (formEntity.container.querySelector("[data-neotes-nmn-purchase]")) {
          formEntity.container.dispatchEvent(new CustomEvent("neotes:nmn:theme-sync"));
        } else updateAddToCart(formEntity.elements, variant);
      });
    });
  }

  function getSelectedOptionTextValues(container) {
    const optionContainer = container.querySelector(".js-product-options") || container;
    const controls = [...optionContainer.querySelectorAll("select[data-option], input[data-option]:checked")];

    return controls.map(el => {
      if (el.tagName === "SELECT") {
        return el.value;
      }
      return el.value;
    });
  }

  function findVariantBySelectedOptions(product, selectedValues) {
    if (!product?.variants || !selectedValues?.length) return null;

    return product.variants.find(variant =>
      Array.isArray(variant.options) &&
      variant.options.length === selectedValues.length &&
      variant.options.every((value, index) => String(value) === String(selectedValues[index]))
    ) || null;
  }

  function findCurrentVariant(container) {
    const el = container?.querySelector(selectors.variantElemJSON);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function getVariantFromUrl(product) {
    const id = new URL(window.location.href).searchParams.get("variant");
    if (!id || !product?.variants) return null;
    return product.variants.find(v => String(v.id) === String(id)) || null;
  }

  function getCurrentVariantFromForm(formEntity) {
    if (formEntity.container.querySelector("[data-neotes-nmn-purchase]")) {
      const id = formEntity.container.querySelector("[data-nmn-variant]")?.value;
      return formEntity.product.variants.find(variant => String(variant.id) === id) || null;
    }
    const selectedValues = getSelectedOptionTextValues(formEntity.container);

    return (
      findVariantBySelectedOptions(formEntity.product, selectedValues) ||
      getVariantFromUrl(formEntity.product) ||
      formEntity.product.selected_variant ||
      findCurrentVariant(formEntity.container)
    );
  }

  function restoreCheckedState(container, selectedOptionValueIds) {
    const optionContainer = container.querySelector(".js-product-options");
    if (!optionContainer) return;

    // Remove stale checked attributes/properties first.
    optionContainer.querySelectorAll('input[data-option][type="radio"]').forEach(input => {
      input.checked = false;
      input.removeAttribute("checked");
    });

    selectedOptionValueIds.forEach(optionValueId => {
      const input = optionContainer.querySelector(
        `input[data-option][data-option-value-id="${CSS.escape(String(optionValueId))}"]`
      );
      if (input) {
        input.checked = true;
        input.setAttribute("checked", "checked");
      }
    });
  }

  async function onChangeForm({ currentTarget: form, target, isTrusted }) {
    const formEntity = forms.find(item => item.container === form);
    if (!formEntity || !target) return;

    if (!target.hasAttribute("data-option")) return;

    const productSection = form.closest(selectors.section);
    const sectionId = productSection?.getAttribute("data-section-id");
    const productUrl = productSection?.getAttribute("data-url");
    const productOptionsContainer = form.querySelector(".js-product-options");

    if (!sectionId || !productUrl || !productOptionsContainer) return;

    // Capture user's actual state BEFORE Shopify replaces the options HTML.
    const selectedOptionTextValues = getSelectedOptionTextValues(form);
    const selectedOptionValueIds = [...productOptionsContainer.querySelectorAll(
      "select[data-option] option:checked, input[data-option]:checked"
    )].map(el => el.dataset.optionValueId).filter(Boolean);

    const params = selectedOptionValueIds.length
      ? `&option_values=${selectedOptionValueIds.join(",")}`
      : "";

    const url = `${productUrl}?section_id=${sectionId}${params}`;
    let responseText = cachedOptions.get(url);

    if (!responseText) {
      try {
        fetchController.abort();
        fetchController = new AbortController();
        const response = await fetch(url, { signal: fetchController.signal });
        responseText = await response.text();
        cachedOptions.set(url, responseText);
      } catch {
        return;
      }
    }

    const html = new DOMParser().parseFromString(responseText, "text/html");
    const freshOptions = html.querySelector(".js-product-options");
    if (!freshOptions) return;

    productOptionsContainer.innerHTML = freshOptions.innerHTML;

    // CRITICAL FIX: Shopify's section HTML can lose radio :checked state.
    // Re-apply exactly what the customer clicked.
    restoreCheckedState(form, selectedOptionValueIds);

    // Determine variant from user's selected option TEXT, not from Shopify's
    // potentially stale/fallback [data-selected-variant].
    let currentVariant =
      findVariantBySelectedOptions(formEntity.product, selectedOptionTextValues) ||
      findCurrentVariant(productOptionsContainer);

    // Keep JSON and hidden variant ID synchronized.
    const selectedVariantJSON = productOptionsContainer.querySelector(selectors.variantElemJSON);
    if (selectedVariantJSON && currentVariant) {
      selectedVariantJSON.textContent = JSON.stringify(currentVariant);
    }

    if (currentVariant) {
      const idInput = form.querySelector(selectors.variantId);
      if (idInput) idInput.value = currentVariant.id;
    }

    updateForm({
      currentVariant,
      elements: formEntity.elements,
      form,
      product: formEntity.product,
      isTrusted
    });

    if (currentVariant) {
      window.themeCore.EventBus.emit(`pdp:section-${sectionId}:change-variant`, {
        variantId: currentVariant.id,
        variant: currentVariant,
        product: formEntity.product,
        firstInitialization: false
      });
    }

    const oldInventory = form.querySelector(selectors.productInventoryWrapper);
    const newInventory = html.querySelector(selectors.productInventoryWrapper);
    if (oldInventory && newInventory) {
      oldInventory.outerHTML = newInventory.outerHTML;
    }

    if (form.dataset.enableHistoryState && currentVariant) {
      const newUrl = getUrlWithVariant(window.location.href, currentVariant.id);
      window.history.replaceState({ path: newUrl }, "", newUrl);
    }
  }

  function updateForm({ currentVariant, elements, form, product, isTrusted }) {
    if (form.querySelector("[data-neotes-nmn-purchase]")) {
      form.dispatchEvent(new CustomEvent("neotes:nmn:theme-sync"));
      return;
    }
    if (!isTrusted) return;

    updateSku(elements, currentVariant);
    updatePrice(elements, currentVariant);
    updateVariantId(elements, currentVariant);
    updateErrorMessages(elements);
    updateAddToCart(elements, currentVariant);
    updatePickupAvailability(currentVariant, form);
    updateSalePercentageBadge(product, currentVariant, form);
  }

  function updateVariantId({ variantIdContainer }, variant) {
    if (variantIdContainer && variant) variantIdContainer.value = variant.id;
  }

  function updateErrorMessages({ quantityError }) {
    if (quantityError) quantityError.innerHTML = "";
  }

  function updateAddToCart({ submit }, variant) {
    const soldOut = window.themeCore.translations.get("products.product.sold_out");
    const unavailable = window.themeCore.translations.get("products.product.unavailable");
    const isPreorder = submit.some(button => button.hasAttribute("data-preorder"));
    const addToCart = isPreorder
      ? window.themeCore.translations.get("products.product.pre_order")
      : window.themeCore.translations.get("products.product.add_to_cart");

    submit.forEach(button => {
      if (variant?.available) {
        button.removeAttribute("disabled");
        button.classList.remove("disabled");
        button.innerText = addToCart;
      } else {
        button.setAttribute("disabled", "disabled");
        button.classList.add("disabled");
        button.innerText = variant ? soldOut : unavailable;
      }
    });
  }

  function updatePickupAvailability(variant, form) {
    const el = form.querySelector("pickup-availability");
    if (!el) return;

    if (variant?.available) {
      el.fetchAvailability(variant.id);
    } else {
      el.removeAttribute("available");
      el.innerHTML = "";
    }
  }

  function updateSku({ skuContainer }, variant) {
    if (!skuContainer) return;

    const sku = variant?.sku;
    const wrapper = skuContainer.closest(selectors.skuWrapper);

    if (!sku) {
      skuContainer.innerHTML = "";
      skuContainer.classList.add(classes.hidden);
      wrapper?.classList.add(classes.hidden);
      return;
    }

    const skuText = skuContainer.dataset.skuText;
    skuContainer.innerHTML = skuText
      ? skuText.replaceAll("{SKU}", sku).replaceAll("{sku}", sku)
      : sku;

    skuContainer.classList.remove(classes.hidden);
    wrapper?.classList.remove(classes.hidden);
  }

  function updatePrice({ priceContainers }, variant) {
    if (!variant) {
      priceContainers.forEach(el => el.classList.add(classes.hidePrice));
      return;
    }

    priceContainers.forEach(priceContainer => {
      const format = priceContainer.hasAttribute(attributes.isCurrencyEnabled)
        ? window.themeCore.objects.shop.money_with_currency_format
        : window.themeCore.objects.shop.money_format;

      const onSale = variant.compare_at_price > variant.price;
      priceContainer.classList.remove(classes.hidePrice);
      priceContainer.classList.toggle(classes.onSale, onSale);

      priceContainer.querySelectorAll(selectors.productPrice).forEach(el => {
        el.innerHTML = formatMoney(variant.price, format);
      });

      priceContainer.querySelectorAll(selectors.productPriceOld).forEach(el => {
        el.innerHTML = formatMoney(variant.compare_at_price, format);
      });
    });
  }

  function updateSalePercentageBadge(product, variant, form) {
    const section = form.closest(selectors.section);
    if (!section) return;

    section.querySelectorAll(selectors.productSalePercentageBadge).forEach(badge => {
      const value = badge.querySelector(selectors.productSalePercentage);
      if (!variant || !product || !variant.available || !value || !(variant.compare_at_price > variant.price)) {
        badge.classList.add(classes.hidden);
        return;
      }

      const salePercentageValue = Math.floor(variant.price * 100 / variant.compare_at_price);
      value.innerHTML = `-${100 - salePercentageValue}% ${window.themeCore.translations.get("products.product.sale")}`;
      badge.classList.remove(classes.hidden);
    });
  }

  function validateCustomFields(arr) {
    let error = false;
    arr.forEach(obj => {
      if (!obj.input || !obj.checkbox) return;
      const inputValue = obj.input.value.trim();

      if (obj.checkbox.checked && !inputValue.length) {
        obj.input.classList.add("error");
        error = true;
      } else if (!obj.checkbox.checked && inputValue.length) {
        obj.checkbox.classList.add("error");
        error = true;
      } else {
        obj.input.classList.remove("error");
        obj.checkbox.classList.remove("error");
      }
    });
    return error;
  }

  function onFormSubmit(event) {
    event.preventDefault();
    const target = event.target;
    const formEntity = forms.find(f => f.id === target.getAttribute(attributes.id));
    if (!formEntity) return;

    const nmn = target.querySelector("[data-neotes-nmn-purchase]");
    if (nmn && (!nmn.neotesValidatePurchase || !nmn.neotesValidatePurchase())) return;
    if (validateCustomFields(formEntity.elements.customFieldElements)) return;

    const currentVariant = getCurrentVariantFromForm(formEntity);
    if (!currentVariant?.available) {
      updateAddToCart(formEntity.elements, currentVariant);
      return;
    }

    formEntity.elements.submit.forEach(button => button.classList.add(classes.loading));

    const serialized = convertFormData(new FormData(target));
    window.themeCore.CartApi
      .makeRequest(window.themeCore.CartApi.actions.ADD_TO_CART, serialized)
      .then(() => {
        formEntity.elements.submit.forEach(button => button.classList.remove(classes.loading));
        window.themeCore.CartApi.makeRequest(window.themeCore.CartApi.actions.GET_CART);
      })
      .catch(error => {
        if (formEntity.elements.quantityError) {
          formEntity.elements.quantityError.innerHTML = error?.description || "";
        }
        formEntity.elements.submit.forEach(button => button.classList.remove(classes.loading));
      });
  }

  function initFirstState() {
    forms.forEach(formEntity => {
      const selectedValues = getSelectedOptionTextValues(formEntity.container);
      const currentVariant =
        (formEntity.container.querySelector("[data-neotes-nmn-purchase]") ? getCurrentVariantFromForm(formEntity) : null) ||
        getVariantFromUrl(formEntity.product) ||
        findVariantBySelectedOptions(formEntity.product, selectedValues) ||
        formEntity.product.selected_variant ||
        findCurrentVariant(formEntity.container);

      updateForm({
        currentVariant,
        elements: formEntity.elements,
        form: formEntity.container,
        product: formEntity.product,
        isTrusted: true
      });
    });
  }

  function showOptions() {
    containers.forEach(form => {
      const variants = form.querySelector(selectors.variants);
      if (variants && variants.dataset.jsProductVariant !== "no-hidden") {
        variants.classList.add(cssClasses.hidden);
      }
    });
  }

  function initStickyBar() {
    containers.forEach(form => {
      const stickyBar = form.querySelector(selectors.stickyBar);
      const mainButton = form.querySelector(selectors.submit);
      if (!stickyBar || !mainButton) return;

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const rect = mainButton.getBoundingClientRect();
          stickyBar.classList.toggle(
            window.themeCore.utils.cssClasses.active,
            !entry.isIntersecting && rect.bottom < 0
          );
        });
      });

      observer.observe(mainButton);
    });
  }

  function initRecipientForm() {
    containers.forEach(form => {
      const checkbox = form.querySelector(selectors.recipientCheckbox);
      const fieldsContainer = form.querySelector(selectors.recipientFieldsContainer);
      const fields = form.querySelectorAll(selectors.recipientField);
      const offset = form.querySelector(selectors.recipientTimeZoneOffset);
      const noJsControl = form.querySelector(selectors.recipientNoJsControl);

      if (!checkbox || !fieldsContainer || !offset) return;

      offset.value = new Date().getTimezoneOffset().toString();
      if (noJsControl) noJsControl.disabled = true;
      checkbox.disabled = false;

      const sync = () => {
        fields.forEach(field => field.disabled = !checkbox.checked);
        offset.disabled = !checkbox.checked;
        fieldsContainer.classList.toggle(classes.hidden, !checkbox.checked);
      };

      sync();
      checkbox.addEventListener("change", sync);
    });
  }

  function initDiscountCodeButton() {
    containers.forEach(form => {
      const button = form.querySelector(selectors.discountButton);
      if (!button) return;

      let copied = false;
      button.addEventListener("click", () => {
        if (copied) return;
        const code = form.querySelector(selectors.discountCode);
        if (!code) return;
        navigator.clipboard?.writeText(code.textContent || "");
        copied = true;
      });
    });
  }

  function allForms() {
    return forms;
  }

  return Object.freeze({ init, allForms });
};

const ProductCarousel = ({ config, selectors: customSelectors, sectionId }) => {
  const Swiper = window.themeCore.utils.Swiper;
  const extendDefaults = window.themeCore.utils.extendDefaults;

  customSelectors = extendDefaults({
    slider: ".js-product-media-slider",
    sliderNavigationNext: ".js-product-media-slider-next",
    sliderNavigationPrev: ".js-product-media-slider-prev",
    thumbNavigationNext: ".js-product-media-thumb-next",
    thumbNavigationPrev: ".js-product-media-thumb-prev",
    sliderSlideVariantId: ".js-product-gallery-slide-variant",
    sliderThumbnails: ".js-product-media-slider-thumbnails",
    activeClass: ".swiper-slide-active",
    modelPoster: ".js-product-media-model-poster",
    notInitedIframe: ".js-video.js-video-youtube, .js-video:empty"
  }, customSelectors);

  let Slider = null;
  let Thumbnails = null;
  let initiated = false;
  let firstLoad = true;

  function init() {
    if (initiated) return Slider;

    const mainSlider = document.querySelector(customSelectors.slider);
    const sliderThumbnails = document.querySelector(customSelectors.sliderThumbnails);
    if (!mainSlider || !sliderThumbnails) return;

    const direction = sliderThumbnails.dataset.thumbnailsPosition === "right" ? "vertical" : "horizontal";

    Thumbnails = new Swiper(customSelectors.sliderThumbnails, {
      direction: direction === "vertical" ? "horizontal" : "horizontal",
      slidesPerView: 6,
      spaceBetween: 4,
      freeMode: true,
      watchSlidesProgress: true,
      threshold: 10,
      breakpoints: direction === "vertical" ? {
        1200: { direction: "vertical", slidesPerView: "auto", spaceBetween: 16 }
      } : {
        1200: { slidesPerView: 6, spaceBetween: 16 }
      }
    });

    Slider = new Swiper(customSelectors.slider, {
      ...config,
      autoHeight: mainSlider.dataset.autoHeight === "true",
      spaceBetween: 8,
      navigation: {
        nextEl: document.querySelector(customSelectors.sliderNavigationNext),
        prevEl: document.querySelector(customSelectors.sliderNavigationPrev)
      },
      thumbs: { swiper: Thumbnails }
    });

    Slider.on("slideChange", swiper => {
      window.themeCore.EventBus.emit("product-slider:slide-change");
      disableTabulationOnNotActiveSlidesWithModel(swiper);
    });

    window.themeCore.EventBus.listen(`pdp:section-${sectionId}:change-variant`, ({ variant }) => {
      if (!Slider || !variant?.featured_media) return;

      const index = [...Slider.slides].findIndex(slide => {
        const el = slide.querySelector(customSelectors.sliderSlideVariantId);
        return el && el.getAttribute("data-media-id")?.includes(variant.featured_media.id);
      });

      if (index < 0) return;

      if (firstLoad && Slider.el.hasAttribute("data-is-show-first-media-image")) {
        firstLoad = false;
        return;
      }

      Slider.slideTo(index);
    });

    initiated = true;
    return Slider;
  }

  function destroy() {
    if (Slider && initiated) Slider.destroy();
    if (Thumbnails && initiated) Thumbnails.destroy();
    Slider = null;
    Thumbnails = null;
    initiated = false;
  }

  function disableSwipe() {
    if (Slider) Slider.allowTouchMove = false;
  }

  return Object.freeze({ init, destroy, disableSwipe });
};

const ProductMediaScroller = () => Object.freeze({ init() {} });

const ProductStickyForm = section => {
  function init() {
    if (!section) return;
    const containers = [...section.querySelectorAll(".js-product-sticky-container")];
    if (!containers.length) return;
    const container = containers.reduce((a, b) => b.offsetHeight > a.offsetHeight ? a : b);
    container.style.top = "1px";
  }
  return Object.freeze({ init });
};

export {
  ProductCarousel as P,
  ProductStickyForm as a,
  ProductMediaScroller as b,
  ProductForm as c
};
